// @flow
//  For transforming broken urls - since the host becomes our iframe host.
import { LIMIT_TOPICS } from '../rest/limits';
import type { AnswerRecord, CommentRecord, QuestionRecord } from '../records';
import { ANSWER_TYPE, QUESTION_TYPE } from '../constants/custom-content';
import { COMMENT_TYPE } from '../constants/built-in-content';

export const EnvironmentType = {
	PROD: 'prod',
	STAGING: 'staging',
	DEV: 'dev',
	LOCAL: 'local',
	NEBULAE: 'nebulae',
	UNKNOWN: 'unknown',
	FEDRAMP_MODERATE_STAGING: 'fedramp-moderate-staging',
	FEDRAMP_MODERATE_PROD: 'fedramp-moderate-prod',
};

export const getOrigin = () => (typeof window === 'undefined' ? '' : window.location.origin);
export const getHref = () => (typeof window === 'undefined' ? '' : window.location.href);

/**
 * Convert labels to topics.
 * This is required for creating topics on the fly when creating or editing questions.
 * Description is not provided so the label name is used instead.
 * Featured is not provided so the default false is assumed.
 *
 * @param labelNames label names provided
 */
export const convertLabelToTopic = (labelNames: Array<string>) =>
	labelNames.map((label) => toTopic(label));

export const toTopic = (labelName: string) => {
	return {
		name: labelName,
		description: labelName,
		featured: false,
	};
};

export const buildPaginationRequestUrl = (
	url: string,
	pageNumber: number,
	pageSize: number = LIMIT_TOPICS,
	limit: number = LIMIT_TOPICS,
	next: string,
) => {
	const start = pageNumber * pageSize;
	const symbol = url.indexOf('?') >= 0 ? '&' : '?';
	return `${url}${symbol}start=${start}&limit=${limit}&next=${next == null ? '' : next}`;
};

export const buildQueryParams = (params: {}) => {
	const p = [];
	for (const name in params) {
		p.push(`${encodeURIComponent(name)}=${encodeURIComponent(params[name])}`);
	}
	return p.join('&');
};

export const getCursorQueryParam = (link: string): string => {
	if (!link) {
		return '';
	}

	const params = new URL(link, 'http://www.atlassian.com').searchParams;
	return params.get('cursor') || '';
};

class RestError extends Error {
	messageKey: string;
	params: Object;
	devError: Object;
	status: number;
}

/**
 * Process Connect Add-on response.
 * Add-on REST calls are done thru ES6 fetch function which does not throw error automatically, so this code will do it
 * to keep behavior consistent with JS Connect API (AP.require used for Confluence REST calls).
 *
 * @param response response
 * @returns {*}
 */
export const checkForAddonRestError = (response: {
	ok: boolean,
	status: number,
	statusText: string,
	json: Function,
}) => {
	if (!response.ok) {
		return response.json().then((error) => {
			const errorObject = new RestError(error.message || response.statusText);
			errorObject.messageKey = error.messageKey;
			errorObject.params = error.params;
			errorObject.devError = error.devError;
			errorObject.status = response.status;
			throw errorObject;
		});
	} else return response;
};

export const invokeAPRequestWithArgs = (data: any) => {
	AP.request(data);
};

export const setDocumentTitle = (title: string) => {
	AP.page?.setTitle(title);
};

export const requestError = (err: Error, reject: Function) => reject(err);

export const requestSuccess = (data: string, resolve: Function, parse: boolean = true) =>
	resolve(parse === true && data ? JSON.parse(data) : data);

export const getInitialMicrosToken = () => {
	return notNull(
		document.querySelector('.cq-body'),
		'Page is missing required element .cq-body',
	).getAttribute('addon-token-header');
};

export function notNull<T>(obj: ?T, message: string): T {
	if (obj == null) {
		throw new Error(message);
	}
	return obj;
}

function compactArray<T>(array: Array<T | null | void>): Array<T> {
	const result = [];
	for (let i = 0; i < array.length; ++i) {
		const elem = array[i];
		if (elem != null) {
			result.push(elem);
		}
	}
	return result;
}

export { compactArray };

export const logDev = (message: string, params: Object) => {
	if (process.env.NODE_ENV !== 'production') {
		console.error(message, params);
	}
};

export const hasOperation = (
	record: QuestionRecord | AnswerRecord,
	type: string,
	target: string,
): boolean => {
	return record.operations.get(type) ? record.operations.get(type).contains(target) : false;
};

export const fetchAllPaginated = async (fetchFn, params) => {
	const result = await fetchFn(params);
	if (result.type.includes('_ERROR')) return [];

	let nextUrl = result.payload?._links?.next;
	let list = result.payload?.results?.results;

	while (nextUrl) {
		params.nextUrl = nextUrl;
		const result = await fetchFn(params);
		if (result.type.includes('_ERROR')) return [];
		list = list.concat(result.payload?.results?.results);
		nextUrl = result.payload?._links?.next;
	}

	return list;
};

export const canEditQuestion = (record: QuestionRecord): boolean => {
	return hasOperation(record, 'update', QUESTION_TYPE);
};

export const canEditAnswer = (record: AnswerRecord): boolean => {
	return hasOperation(record, 'update', ANSWER_TYPE);
};

export const canRemoveQuestion = (record: QuestionRecord): boolean => {
	return hasOperation(record, 'delete', QUESTION_TYPE);
};

export const canRemoveAnswer = (record: AnswerRecord): boolean => {
	return hasOperation(record, 'delete', ANSWER_TYPE);
};

export const canCreateAnswer = (record: QuestionRecord): boolean => {
	return hasOperation(record, 'create', ANSWER_TYPE);
};

export const canComment = (record: QuestionRecord | AnswerRecord): boolean => {
	return hasOperation(record, 'create', COMMENT_TYPE);
};

export const canEditComment = (record: CommentRecord): boolean => {
	return hasOperation(record, 'update', COMMENT_TYPE);
};

export const canRemoveComment = (record: CommentRecord): boolean => {
	return hasOperation(record, 'delete', COMMENT_TYPE);
};
